import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import AsyncSelect from 'react-select/async';
import { GoogleMap, useJsApiLoader, Marker, Polyline } from '@react-google-maps/api';
import api from '../../services/api';
import toast from 'react-hot-toast';
import Modal from '../../components/common/Modal';
import Table from '../../components/common/Table';
import Pagination from '../../components/common/Pagination';
import { LuPlus, LuPencil, LuTrash2, LuSearch, LuNavigation, LuExternalLink, LuArrowRight, LuLoader, LuPenLine, LuRotateCcw, LuX, LuZap, LuMaximize2, LuMinimize2 } from 'react-icons/lu';
import { debounce, selectStyles } from '../../utils/selectConfig';

const EMPTY = { from: '', to: '', encodedPolyline: '', distanceKm: '', durationMin: '', routeMode: 'auto', status: 'active' };

const MAP_LIBRARIES = ['places', 'geometry'];
const MAP_CONTAINER_NORMAL = { width: '100%', height: '320px', borderRadius: '12px', overflow: 'hidden' };
const MAP_CONTAINER_FULL   = { width: '100%', height: 'calc(100vh - 80px)', borderRadius: '12px', overflow: 'hidden' };
const MAP_DEFAULT_CENTER = { lat: 21.3891, lng: 39.8579 }; // Makkah

// ─── Inline map component ────────────────────────────────────────────────────
function RouteMapPicker({ fromLoc, toLoc, onDirectionsResult, onModeChange, apiKey, skipInitialAutoFetch = false, initialMode = 'auto', initialPolyline = '' }) {
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: apiKey || '',
    libraries: MAP_LIBRARIES,
  });

  const [mode,         setMode]         = useState(initialMode);
  const [autoPath,     setAutoPath]     = useState([]);
  const [fetching,     setFetching]     = useState(false);
  const [mapError,     setMapError]     = useState('');
  const [drawnPath,    setDrawnPath]    = useState([]);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const autoFetchUserTriggered = useRef(false);
  const restoredRef            = useRef(false);
  const mapRef                 = useRef(null);
  const autoPolylineRef        = useRef(null);
  const [mapReady, setMapReady] = useState(false);

  // Imperatively manage the auto-route polyline so cleanup is guaranteed
  useEffect(() => {
    if (!mapReady || !mapRef.current) return;
    if (autoPolylineRef.current) {
      autoPolylineRef.current.setMap(null);
      autoPolylineRef.current = null;
    }
    if (mode === 'auto' && autoPath.length > 0) {
      autoPolylineRef.current = new window.google.maps.Polyline({
        path: autoPath,
        strokeColor: '#4285F4',
        strokeWeight: 5,
        strokeOpacity: 0.85,
        icons: [{ icon: { path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW, scale: 3 }, offset: '50%' }],
        map: mapRef.current,
      });
    }
    return () => {
      if (autoPolylineRef.current) {
        autoPolylineRef.current.setMap(null);
        autoPolylineRef.current = null;
      }
    };
  }, [autoPath, mode, mapReady]);

  // Restore saved path when editing an existing route
  useEffect(() => {
    if (!isLoaded || !initialPolyline || restoredRef.current) return;
    restoredRef.current = true;
    const decoded = window.google.maps.geometry.encoding.decodePath(initialPolyline);
    const path = decoded.map(p => ({ lat: p.lat(), lng: p.lng() }));
    if (initialMode === 'draw') setDrawnPath(path);
    else setAutoPath(path);
  }, [isLoaded, initialMode, initialPolyline]);

  // Auto-route fetch — skips initial fetch when editing to preserve saved values
  useEffect(() => {
    let stale = false;
    setAutoPath([]);
    setMapError('');
    setFetching(false);
    if (mode !== 'auto' || !fromLoc || !toLoc || !isLoaded) return;
    if (skipInitialAutoFetch && !autoFetchUserTriggered.current) return;

    setFetching(true);
    const service = new window.google.maps.DirectionsService();
    service.route(
      {
        origin:      { lat: Number(fromLoc.lat), lng: Number(fromLoc.lng) },
        destination: { lat: Number(toLoc.lat),   lng: Number(toLoc.lng)   },
        travelMode:  window.google.maps.TravelMode.DRIVING,
      },
      (result, status) => {
        if (stale) return;
        setFetching(false);
        if (status === 'OK') {
          const encoded = result.routes[0].overview_polyline;
          const decoded = window.google.maps.geometry.encoding.decodePath(encoded);
          setAutoPath(decoded.map(p => ({ lat: p.lat(), lng: p.lng() })));
          const leg = result.routes[0].legs[0];
          onDirectionsResult({
            distanceKm:      (leg.distance.value / 1000).toFixed(1),
            durationMin:     Math.round(leg.duration.value / 60),
            encodedPolyline: encoded,
          });
        } else {
          setMapError(`Directions unavailable (${status})`);
        }
      }
    );
    return () => { stale = true; };
  }, [fromLoc?.lat, fromLoc?.lng, toLoc?.lat, toLoc?.lng, isLoaded, mode, skipInitialAutoFetch]);

  // Recompute drawn route stats whenever path changes
  useEffect(() => {
    if (mode !== 'draw' || !isLoaded || drawnPath.length < 2) return;
    const gPath = drawnPath.map(p => new window.google.maps.LatLng(p.lat, p.lng));
    const distM  = window.google.maps.geometry.spherical.computeLength(gPath);
    const encoded = window.google.maps.geometry.encoding.encodePath(gPath);
    onDirectionsResult({ distanceKm: (distM / 1000).toFixed(1), durationMin: '', encodedPolyline: encoded });
  }, [drawnPath, mode, isLoaded]);

  // Pan imperatively so re-renders never snap the map back to marker A
  useEffect(() => {
    if (!mapReady || !mapRef.current) return;
    if (fromLoc && toLoc) {
      const bounds = new window.google.maps.LatLngBounds();
      bounds.extend({ lat: Number(fromLoc.lat), lng: Number(fromLoc.lng) });
      bounds.extend({ lat: Number(toLoc.lat),   lng: Number(toLoc.lng)   });
      mapRef.current.fitBounds(bounds, 60);
    } else if (fromLoc) {
      mapRef.current.panTo({ lat: Number(fromLoc.lat), lng: Number(fromLoc.lng) });
      mapRef.current.setZoom(13);
    }
  }, [fromLoc?.lat, fromLoc?.lng, toLoc?.lat, toLoc?.lng, mapReady]);

  const switchMode = (next) => {
    setMode(next);
    onModeChange?.(next);
    if (next === 'auto') {
      autoFetchUserTriggered.current = true;
      setDrawnPath([]);
    } else {
      setAutoPath([]);
      onDirectionsResult({ distanceKm: '', durationMin: '', encodedPolyline: '' });
    }
  };

  const handleMapClick = useCallback((e) => {
    if (mode !== 'draw') return;
    setDrawnPath(prev => [...prev, { lat: e.latLng.lat(), lng: e.latLng.lng() }]);
  }, [mode]);

  const undoPoint = () => {
    setDrawnPath(prev => {
      const next = prev.slice(0, -1);
      if (next.length < 2) onDirectionsResult({ distanceKm: '', durationMin: '', encodedPolyline: '' });
      return next;
    });
  };

  const clearDrawing = () => {
    setDrawnPath([]);
    onDirectionsResult({ distanceKm: '', durationMin: '', encodedPolyline: '' });
  };

  if (!apiKey) {
    return (
      <div className="w-full h-[200px] rounded-xl bg-gray-50 border border-dashed border-gray-200 flex flex-col items-center justify-center gap-2 text-gray-400">
        <LuNavigation size={24} />
        <p className="text-sm font-medium">Google Maps not configured</p>
        <p className="text-xs">Add your Google Maps API Key in <strong>Settings</strong></p>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="w-full h-[200px] rounded-xl bg-red-50 flex items-center justify-center text-red-500 text-sm">
        Failed to load Google Maps
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="w-full h-[200px] rounded-xl bg-gray-50 flex items-center justify-center gap-2 text-gray-400">
        <LuLoader size={18} className="animate-spin" />
        <span className="text-sm">Loading map…</span>
      </div>
    );
  }

  const toolbar = (
    <div className="flex items-center gap-2 flex-wrap">
      <div className="flex rounded-lg border border-gray-200 overflow-hidden text-xs font-semibold">
        <button type="button"
          onClick={() => switchMode('auto')}
          className={`flex items-center gap-1.5 px-3 py-1.5 transition-colors ${mode === 'auto' ? 'bg-hajj-teal text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}>
          <LuZap size={12} /> Auto Route
        </button>
        <button type="button"
          onClick={() => switchMode('draw')}
          className={`flex items-center gap-1.5 px-3 py-1.5 transition-colors ${mode === 'draw' ? 'bg-hajj-teal text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}>
          <LuPenLine size={12} /> Draw Route
        </button>
      </div>

      {mode === 'draw' && drawnPath.length > 0 && (
        <>
          <span className="text-[11px] text-gray-400">{drawnPath.length} point{drawnPath.length !== 1 ? 's' : ''}</span>
          <button type="button" onClick={undoPoint}
            className="flex items-center gap-1 px-2 py-1 text-xs text-gray-500 hover:text-amber-600 hover:bg-amber-50 rounded-lg border border-gray-200 transition-colors">
            <LuRotateCcw size={11} /> Undo
          </button>
          <button type="button" onClick={clearDrawing}
            className="flex items-center gap-1 px-2 py-1 text-xs text-gray-500 hover:text-red-500 hover:bg-red-50 rounded-lg border border-gray-200 transition-colors">
            <LuX size={11} /> Clear
          </button>
        </>
      )}
      {mode === 'auto' && fromLoc && toLoc && (
        <>
          <span className="text-[11px] text-gray-400">Route calculated automatically</span>
          {autoPath.length > 0 && (
            <button
              type="button"
              onClick={() => { setAutoPath([]); onDirectionsResult({ distanceKm: '', durationMin: '', encodedPolyline: '' }); }}
              className="flex items-center gap-1 px-2 py-1 text-xs text-gray-500 hover:text-red-500 hover:bg-red-50 rounded-lg border border-gray-200 transition-colors"
            >
              <LuX size={11} /> Clear Route
            </button>
          )}
        </>
      )}

      <button
        type="button"
        onClick={() => setIsFullscreen(f => !f)}
        title={isFullscreen ? 'Exit fullscreen' : 'Expand map'}
        className="ml-auto flex items-center gap-1 px-2 py-1 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg border border-gray-200 transition-colors"
      >
        {isFullscreen ? <LuMinimize2 size={12} /> : <LuMaximize2 size={12} />}
        <span>{isFullscreen ? 'Minimize' : 'Fullscreen'}</span>
      </button>
    </div>
  );

  const mapEl = (
    <div className="relative rounded-xl overflow-hidden border border-gray-100" onMouseDown={e => { if (mode === 'draw') e.preventDefault(); }}>
      <GoogleMap
        mapContainerStyle={isFullscreen ? MAP_CONTAINER_FULL : MAP_CONTAINER_NORMAL}
        defaultCenter={MAP_DEFAULT_CENTER}
        defaultZoom={7}
        onClick={handleMapClick}
        onLoad={map => { mapRef.current = map; setMapReady(true); }}
        options={{
          streetViewControl: false,
          fullscreenControl: false,
          mapTypeControl: false,
          zoomControlOptions: { position: 9 },
          draggableCursor: mode === 'draw' ? 'crosshair' : undefined,
          clickableIcons: mode !== 'draw',
          gestureHandling: mode === 'draw' ? 'greedy' : 'auto',
        }}
      >
        {fromLoc && (
          <Marker position={{ lat: Number(fromLoc.lat), lng: Number(fromLoc.lng) }}
            label={{ text: 'A', color: 'white', fontWeight: 'bold', fontSize: '12px' }} />
        )}
        {toLoc && (
          <Marker position={{ lat: Number(toLoc.lat), lng: Number(toLoc.lng) }}
            label={{ text: 'B', color: 'white', fontWeight: 'bold', fontSize: '12px' }} />
        )}

        {mode === 'draw' && drawnPath.length > 0 && (
          <Polyline
            path={drawnPath}
            options={{ strokeColor: '#2d5a4a', strokeWeight: 4, strokeOpacity: 0.85,
              icons: [{ icon: { path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW, scale: 3 }, offset: '50%' }] }}
          />
        )}
      </GoogleMap>

      {fetching && (
        <div className="absolute inset-0 bg-white/60 backdrop-blur-[2px] flex items-center justify-center gap-2 text-hajj-dark text-sm font-medium">
          <LuLoader size={16} className="animate-spin" />
          Getting directions…
        </div>
      )}
      {mode === 'draw' && drawnPath.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="bg-white/90 backdrop-blur-sm rounded-xl px-4 py-2.5 text-sm text-gray-500 shadow-sm border border-gray-100 flex items-center gap-2">
            <LuPenLine size={14} className="text-hajj-teal" />
            Click on the map to start drawing the route
          </div>
        </div>
      )}
      {mode === 'auto' && !fromLoc && !toLoc && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="bg-white/90 backdrop-blur-sm rounded-xl px-4 py-2.5 text-sm text-gray-500 shadow-sm border border-gray-100">
            Select From and To locations to plot route
          </div>
        </div>
      )}
      {mapError && (
        <div className="absolute bottom-2 left-2 right-2 bg-red-50 text-red-600 text-xs rounded-lg px-3 py-2 text-center border border-red-100">
          {mapError}
        </div>
      )}
    </div>
  );

  if (isFullscreen) {
    return (
      <div className="fixed inset-0 z-[100] bg-white flex flex-col p-4 gap-3">
        {toolbar}
        {mapEl}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {toolbar}
      {mapEl}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function HajjRoutes() {
  const [routes,      setRoutes]      = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [search,      setSearch]      = useState('');
  const [page,        setPage]        = useState(1);
  const [pagination,  setPagination]  = useState(null);
  const PAGE_SIZE = 10;
  const [modal,       setModal]       = useState({ open: false, mode: 'add', data: null });
  const [form,        setForm]        = useState(EMPTY);
  const [saving,      setSaving]      = useState(false);
  const [mapsApiKey,  setMapsApiKey]  = useState('');
  const [fromOpt,     setFromOpt]     = useState(null);
  const [toOpt,       setToOpt]       = useState(null);

  const loadStatic = useCallback(async () => {
    const sRes = await api.get('/settings/google_maps_api_key').catch(() => null);
    if (sRes) setMapsApiKey(sRes.data.data?.value || '');
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: PAGE_SIZE });
      if (search) params.set('search', search);
      const { data } = await api.get(`/routes?${params}`);
      setRoutes(data.data || []);
      setPagination(data.pagination || null);
    } catch { toast.error('Failed to load routes'); }
    finally { setLoading(false); }
  }, [page, search]);

  useEffect(() => { loadStatic(); }, [loadStatic]);
  useEffect(() => { load(); }, [load]);

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const locFetcher = async (q) => {
    const { data } = await api.get(`/locations?status=active&search=${encodeURIComponent(q)}&limit=10`);
    return (data.data || []).map(l => ({ value: l._id, label: l.name, lat: l.lat, lng: l.lng }));
  };
  const loadLocationsFrom = useMemo(() => debounce(locFetcher, 300), []); // separate instances intentional
  const loadLocationsTo   = useMemo(() => debounce(locFetcher, 300), []);

  const openAdd  = () => { setForm(EMPTY); setFromOpt(null); setToOpt(null); setModal({ open: true, mode: 'add', data: null }); };
  const openEdit = r => {
    setForm({
      from: r.from?._id || r.from || '',
      to:   r.to?._id   || r.to   || '',
      encodedPolyline: r.encodedPolyline || '',
      distanceKm:      r.distanceKm  || '',
      durationMin:     r.durationMin || '',
      routeMode:       r.routeMode   || 'auto',
      status: r.status,
    });
    setFromOpt(r.from?._id ? { value: r.from._id, label: r.from.name, lat: r.from.lat, lng: r.from.lng } : null);
    setToOpt(r.to?._id     ? { value: r.to._id,   label: r.to.name,   lat: r.to.lat,   lng: r.to.lng   } : null);
    setModal({ open: true, mode: 'edit', data: r });
  };
  const close = () => { setModal({ open: false, mode: 'add', data: null }); setFromOpt(null); setToOpt(null); };

  const handleDirections = ({ distanceKm, durationMin, encodedPolyline }) => {
    setForm(f => ({ ...f, distanceKm, durationMin, encodedPolyline }));
  };


  const handleSubmit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const payload = { ...form };
      if (payload.distanceKm === '') delete payload.distanceKm;
      if (payload.durationMin === '') delete payload.durationMin;
      if (modal.mode === 'add') await api.post('/routes', payload);
      else await api.put(`/routes/${modal.data._id}`, payload);
      toast.success(modal.mode === 'add' ? 'Route created!' : 'Updated!');
      close(); load();
    } catch (err) { toast.error(err.response?.data?.message || 'Error saving'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this route?')) return;
    try { await api.delete(`/routes/${id}`); toast.success('Deleted'); load(); }
    catch { toast.error('Delete failed'); }
  };

  const mapsUrl = (fromLoc, toLoc) => {
    if (!fromLoc?.lat || !toLoc?.lat) return null;
    return `https://www.google.com/maps/dir/${fromLoc.lat},${fromLoc.lng}/${toLoc.lat},${toLoc.lng}`;
  };

  const prevSearch = useRef(search);
  useEffect(() => { if (prevSearch.current !== search) { prevSearch.current = search; setPage(1); } });

  const fromLoc = fromOpt;
  const toLoc   = toOpt;

  const columns = [
    { key: 'route', label: 'From → To', render: r => (
      <div className="flex items-center gap-1.5 text-sm">
        <span className="font-medium text-hajj-dark">{r.from?.name || '—'}</span>
        <LuArrowRight size={13} className="text-hajj-gold flex-shrink-0" />
        <span className="font-medium text-hajj-dark">{r.to?.name || '—'}</span>
      </div>
    )},
    { key: 'distance', label: 'Distance', render: r => r.distanceKm
      ? <span className="badge bg-gray-100 text-gray-600">{r.distanceKm} km</span>
      : <span className="text-gray-300">—</span>
    },
    { key: 'duration', label: 'Duration', render: r => r.durationMin
      ? <span className="badge bg-hajj-teal/10 text-teal-700">{r.durationMin} min</span>
      : <span className="text-gray-300">—</span>
    },
    { key: 'status', label: 'Status', render: r => (
      <span className={`badge ${r.status === 'active' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'}`}>{r.status.charAt(0).toUpperCase() + r.status.slice(1)}</span>
    )},
    { key: 'actions', label: '', width: 100, render: r => (
      <div className="flex gap-1.5">
        <button onClick={() => { const u = mapsUrl(r.from, r.to); if (u) window.open(u, '_blank'); }}
          title="View on Google Maps"
          className="w-8 h-8 rounded-lg bg-blue-50 text-blue-500 flex items-center justify-center hover:bg-blue-100 transition-colors">
          <LuExternalLink size={14} />
        </button>
        <button onClick={() => openEdit(r)}
          className="w-8 h-8 rounded-lg bg-hajj-teal/10 text-teal-700 flex items-center justify-center hover:bg-hajj-teal/20 transition-colors">
          <LuPencil size={14} />
        </button>
        <button onClick={() => handleDelete(r._id)}
          className="w-8 h-8 rounded-lg bg-red-50 text-red-500 flex items-center justify-center hover:bg-red-100 transition-colors">
          <LuTrash2 size={14} />
        </button>
      </div>
    )},
  ];

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2.5 text-hajj-dark">
          <LuNavigation className="text-xl sm:text-2xl" />
          <h1 className="text-xl sm:text-2xl font-extrabold">Routes</h1>
        </div>
        <button className="btn-primary text-sm" onClick={openAdd}><LuPlus size={15} /> Add Route</button>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center gap-2.5">
        <div className="relative w-full sm:max-w-xs">
          <LuSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={15} />
          <input placeholder="Search routes…" value={search} onChange={e => setSearch(e.target.value)} className="input-field pl-9 w-full" />
        </div>
        <span className="text-sm text-gray-400 sm:ml-auto">{pagination?.total ?? routes.length} routes</span>
      </div>

      <div className="card">
        <Table columns={columns} data={routes} loading={loading} emptyMessage="No routes found" />
        <Pagination page={page} total={pagination?.total || 0} pageSize={PAGE_SIZE} onPage={setPage} />
      </div>

      <Modal isOpen={modal.open} onClose={close} title={modal.mode === 'add' ? 'Add Route' : 'Edit Route'} size="xl">
        <form onSubmit={handleSubmit} className="flex flex-col gap-5">

          {/* Fields row */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">From *</label>
              <AsyncSelect
                loadOptions={loadLocationsFrom}
                defaultOptions
                cacheOptions
                isClearable
                placeholder="Search location..."
                value={fromOpt}
                filterOption={opt => opt.value !== form.to}
                onChange={opt => { setFromOpt(opt); setForm(f => ({ ...f, from: opt?.value || '' })); }}
                styles={selectStyles}
                menuPortalTarget={document.body}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">To *</label>
              <AsyncSelect
                loadOptions={loadLocationsTo}
                defaultOptions
                cacheOptions
                isClearable
                placeholder="Search location..."
                value={toOpt}
                filterOption={opt => opt.value !== form.from}
                onChange={opt => { setToOpt(opt); setForm(f => ({ ...f, to: opt?.value || '' })); }}
                styles={selectStyles}
                menuPortalTarget={document.body}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Status</label>
              <select value={form.status} onChange={set('status')} className="input-field">
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          {/* Map */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Route Map</label>
            <RouteMapPicker
              key={modal.data?._id || 'new'}
              fromLoc={fromLoc}
              toLoc={toLoc}
              onDirectionsResult={handleDirections}
              onModeChange={m => setForm(f => ({ ...f, routeMode: m }))}
              apiKey={mapsApiKey}
              skipInitialAutoFetch={modal.mode === 'edit'}
              initialMode={form.routeMode}
              initialPolyline={form.encodedPolyline}
            />
          </div>

          {/* Auto-filled stats */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                Distance (km)
                {form.distanceKm && <span className="ml-1.5 text-[10px] text-green-600 font-normal normal-case">auto-filled</span>}
              </label>
              <input
                type="number" step="0.1" min="0"
                value={form.distanceKm}
                onChange={set('distanceKm')}
                className="input-field"
                placeholder="Auto-filled from map"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                Duration (minutes)
                {form.durationMin && <span className="ml-1.5 text-[10px] text-green-600 font-normal normal-case">auto-filled</span>}
              </label>
              <input
                type="number" min="0"
                value={form.durationMin}
                onChange={set('durationMin')}
                className="input-field"
                placeholder="Auto-filled from map"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                Encoded Polyline
                {form.encodedPolyline && <span className="ml-1.5 text-[10px] text-green-600 font-normal normal-case">auto-filled from map</span>}
              </label>
              <textarea
                value={form.encodedPolyline}
                onChange={set('encodedPolyline')}
                rows={2}
                className="input-field resize-none font-mono text-xs"
                placeholder="Auto-filled when route is plotted on map"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2 border-t border-gray-100">
            <button type="button" className="btn-secondary" onClick={close}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving && <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
              {modal.mode === 'add' ? 'Add Route' : 'Save Changes'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
